import { MediaType } from '@qse/api'
import type { FileTypeIconProps } from '@qse/constants'
import React from 'react'

const getMediaTypeByExtension = (filename: string): MediaType => {
  if (!filename) return MediaType.OTHER
  const extension = filename.split('.').pop()?.toLowerCase()

  if (!extension) return MediaType.OTHER

  const ext = extension.replace('.', '')

  // Images
  if (
    [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'bmp',
      'tiff',
      'webp',
      'svg',
      'ico',
      'heic',
      'heif',
      'avif',
    ].includes(ext)
  ) {
    return MediaType.IMAGE
  }

  // Videos
  if (
    [
      'mp4',
      'webm',
      'mov',
      'avi',
      'mkv',
      'mpg',
      'mpeg',
      '3gp',
      'flv',
      'wmv',
      'asf',
      'rm',
      'rmvb',
      'mxf',
    ].includes(ext)
  ) {
    return MediaType.VIDEO
  }

  // Audio
  if (
    [
      'mp3',
      'mp2',
      'wav',
      'ogg',
      'aac',
      'flac',
      'm4a',
      'wma',
      'aiff',
      'au',
      'ra',
      'mid',
      'midi',
      'sln',
      'ircam',
      'caf',
      'm3u',
      'm4b',
      'm4p',
      'm4r',
      'm4v',
      'mka',
      'mks',
      'm3u8',
    ].includes(ext)
  ) {
    return MediaType.AUDIO
  }

  // PDF
  if (ext === 'pdf') {
    return MediaType.PDF
  }

  // Spreadsheets
  if (['xls', 'xlsx', 'csv', 'tsv', 'ods', 'numbers'].includes(ext)) {
    return MediaType.SPREADSHEET
  }

  // Presentations
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)) {
    return MediaType.PRESENTATION
  }

  // Documents
  if (['doc', 'docx'].includes(ext)) {
    return MediaType.DOCUMENT
  }

  // Archives
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'lzma', 'zstd'].includes(ext)) {
    return MediaType.ARCHIVE
  }

  return MediaType.OTHER
}

// Simple MIME type to icon mapping
const MIME_TYPE_ICONS: Record<string, string> = {
  // Images
  'image/jpeg': '/assets/svg/file-types/picture.svg',
  'image/jpg': '/assets/svg/file-types/picture.svg',
  'image/png': '/assets/svg/file-types/picture.svg',
  'image/gif': '/assets/svg/file-types/picture.svg',
  'image/bmp': '/assets/svg/file-types/picture.svg',
  'image/tiff': '/assets/svg/file-types/picture.svg',
  'image/webp': '/assets/svg/file-types/picture.svg',
  'image/svg+xml': '/assets/svg/file-types/picture.svg',
  'image/ico': '/assets/svg/file-types/picture.svg',
  'image/heic': '/assets/svg/file-types/picture.svg',
  'image/heif': '/assets/svg/file-types/picture.svg',
  'image/avif': '/assets/svg/file-types/picture.svg',

  // Videos
  'video/mp4': '/assets/svg/file-types/video.svg',
  'video/webm': '/assets/svg/file-types/video.svg',
  'video/quicktime': '/assets/svg/file-types/video.svg',
  'video/avi': '/assets/svg/file-types/video.svg',
  'video/x-matroska': '/assets/svg/file-types/video.svg',
  'video/mpeg': '/assets/svg/file-types/video.svg',
  'video/3gpp': '/assets/svg/file-types/video.svg',
  'video/x-flv': '/assets/svg/file-types/video.svg',
  'video/x-ms-wmv': '/assets/svg/file-types/video.svg',
  'video/x-ms-asf': '/assets/svg/file-types/video.svg',
  'application/vnd.rn-realmedia': '/assets/svg/file-types/video.svg',
  'application/vnd.rn-realmedia-vbr': '/assets/svg/file-types/video.svg',
  'application/mxf': '/assets/svg/file-types/video.svg',

  // Audio
  'audio/mpeg': '/assets/svg/file-types/audio.svg',
  'audio/wav': '/assets/svg/file-types/audio.svg',
  'audio/ogg': '/assets/svg/file-types/audio.svg',
  'audio/aac': '/assets/svg/file-types/audio.svg',
  'audio/flac': '/assets/svg/file-types/audio.svg',
  'audio/x-m4a': '/assets/svg/file-types/audio.svg',
  'audio/webm': '/assets/svg/file-types/audio.svg',
  'audio/x-matroska': '/assets/svg/file-types/audio.svg',
  'audio/x-realaudio': '/assets/svg/file-types/audio.svg',
  'audio/x-midi': '/assets/svg/file-types/audio.svg',
  'audio/midi': '/assets/svg/file-types/audio.svg',
  'audio/x-ircam': '/assets/svg/file-types/audio.svg',
  'audio/x-caf': '/assets/svg/file-types/audio.svg',
  'audio/x-mpegurl': '/assets/svg/file-types/audio.svg',
  'audio/x-sndfile': '/assets/svg/file-types/audio.svg',
  'audio/x-m4b': '/assets/svg/file-types/audio.svg',
  'audio/x-m4p': '/assets/svg/file-types/audio.svg',
  'audio/x-m4r': '/assets/svg/file-types/audio.svg',
  'audio/x-m4v': '/assets/svg/file-types/audio.svg',
  'audio/x-ms-wma': '/assets/svg/file-types/audio.svg',
  'audio/x-aiff': '/assets/svg/file-types/audio.svg',
  'audio/basic': '/assets/svg/file-types/audio.svg',

  // Documents
  'application/pdf': '/assets/svg/file-types/pdf.svg',
  'application/msword': '/assets/svg/file-types/docs.svg',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    '/assets/svg/file-types/docs.svg',
  'application/vnd.ms-excel': '/assets/svg/file-types/excel.svg',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    '/assets/svg/file-types/excel.svg',
  'application/vnd.ms-powerpoint': '/assets/svg/file-types/ppt.svg',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    '/assets/svg/file-types/ppt.svg',
  'application/vnd.apple.numbers': '/assets/svg/file-types/excel.svg',
  'application/vnd.apple.keynote': '/assets/svg/file-types/ppt.svg',
  'application/vnd.apple.pages': '/assets/svg/file-types/docs.svg',

  // Text and Code
  'text/plain': '/assets/svg/file-text.svg',
  'text/markdown': '/assets/svg/file-text.svg',
  'text/csv': '/assets/svg/file-types/excel.svg',
  'text/tab-separated-values': '/assets/svg/file-types/excel.svg',
  'text/html': '/assets/svg/file-text.svg',
  'text/css': '/assets/svg/file-text.svg',
  'text/javascript': '/assets/svg/file-text.svg',
  'application/json': '/assets/svg/file-text.svg',
  'application/xml': '/assets/svg/file-text.svg',
  'text/xml': '/assets/svg/file-text.svg',

  // Archives
  'application/zip': '/assets/svg/file-types/archives.svg',
  'application/x-rar-compressed': '/assets/svg/file-types/archives.svg',
  'application/vnd.rar': '/assets/svg/file-types/archives.svg',
  'application/x-7z-compressed': '/assets/svg/file-types/archives.svg',
  'application/x-tar': '/assets/svg/file-types/archives.svg',
  'application/gzip': '/assets/svg/file-types/archives.svg',
  'application/x-bzip2': '/assets/svg/file-types/archives.svg',
  'application/x-gzip': '/assets/svg/file-types/archives.svg',
  'application/x-zip-compressed': '/assets/svg/file-types/archives.svg',
  'application/x-compressed': '/assets/svg/file-types/archives.svg',
  'application/x-compressed-tar': '/assets/svg/file-types/archives.svg',
  'application/x-compressed-tar-gz': '/assets/svg/file-types/archives.svg',
  'application/x-compressed-tar-bz2': '/assets/svg/file-types/archives.svg',
  'application/x-compressed-tar-xz': '/assets/svg/file-types/archives.svg',
  'application/x-compressed-tar-lzma': '/assets/svg/file-types/archives.svg',
  'application/x-compressed-tar-zstd': '/assets/svg/file-types/archives.svg',
  'application/x-xz': '/assets/svg/file-types/archives.svg',
  'application/x-lzma': '/assets/svg/file-types/archives.svg',
  'application/zstd': '/assets/svg/file-types/archives.svg',
  'application/x-zstd': '/assets/svg/file-types/archives.svg',
}

// Simple extension to MIME type mapping
const EXTENSION_TO_MIME: Record<string, string> = {
  // Images
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  bmp: 'image/bmp',
  tiff: 'image/tiff',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  ico: 'image/ico',
  heic: 'image/heic',
  heif: 'image/heif',
  avif: 'image/avif',

  // Videos
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  avi: 'video/avi',
  mkv: 'video/x-matroska',
  mpg: 'video/mpeg',
  mpeg: 'video/mpeg',
  '3gp': 'video/3gpp',
  flv: 'video/x-flv',
  wmv: 'video/x-ms-wmv',
  asf: 'video/x-ms-asf',
  rm: 'application/vnd.rn-realmedia',
  rmvb: 'application/vnd.rn-realmedia-vbr',
  mxf: 'application/mxf',

  // Audio
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  aac: 'audio/aac',
  flac: 'audio/flac',
  m4a: 'audio/x-m4a',
  mp2: 'audio/mpeg',
  mka: 'audio/x-matroska',
  mks: 'audio/x-matroska',
  m3u8: 'audio/x-mpegurl',
  m4b: 'audio/x-m4b',
  m4p: 'audio/x-m4p',
  m4r: 'audio/x-m4r',
  m4v: 'audio/x-m4v',
  ra: 'audio/x-realaudio',
  mid: 'audio/x-midi',
  midi: 'audio/x-midi',
  ircam: 'audio/x-ircam',
  caf: 'audio/x-caf',
  m3u: 'audio/x-mpegurl',
  sln: 'audio/x-sndfile',
  wma: 'audio/x-ms-wma',
  aiff: 'audio/x-aiff',
  au: 'audio/basic',

  // Documents
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  numbers: 'application/vnd.apple.numbers',
  key: 'application/vnd.apple.keynote',
  pages: 'application/vnd.apple.pages',

  // Text and Code
  txt: 'text/plain',
  md: 'text/markdown',
  mdx: 'text/markdown',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  html: 'text/html',
  htm: 'text/html',
  css: 'text/css',
  js: 'text/javascript',
  ts: 'text/javascript',
  jsx: 'text/javascript',
  tsx: 'text/javascript',
  py: 'text/plain',
  java: 'text/plain',
  c: 'text/plain',
  cpp: 'text/plain',
  h: 'text/plain',
  hpp: 'text/plain',
  cs: 'text/plain',
  php: 'text/plain',
  rb: 'text/plain',
  go: 'text/plain',
  rs: 'text/plain',
  swift: 'text/plain',
  kt: 'text/plain',
  scala: 'text/plain',
  r: 'text/plain',
  sql: 'text/plain',
  yaml: 'text/plain',
  yml: 'text/plain',
  ini: 'text/plain',
  cfg: 'text/plain',
  conf: 'text/plain',
  log: 'text/plain',
  sh: 'text/plain',
  bash: 'text/plain',
  zsh: 'text/plain',
  fish: 'text/plain',
  ps1: 'text/plain',
  bat: 'text/plain',
  cmd: 'text/plain',

  // Archives
  zip: 'application/zip',
  rar: 'application/vnd.rar',
  '7z': 'application/x-7z-compressed',
  tar: 'application/x-tar',
  gz: 'application/gzip',
  bz2: 'application/x-bzip2',
  xz: 'application/x-xz',
  lzma: 'application/x-lzma',
  zstd: 'application/zstd',

  // Executables
  exe: 'application/x-msdownload',
  msi: 'application/x-msi',
  app: 'application/x-mach-binary',
  dmg: 'application/x-apple-diskimage',

  // 3D and CAD
  obj: 'model/obj',
  stl: 'model/stl',
  gltf: 'model/gltf+json',
  glb: 'model/gltf-binary',
  dxf: 'application/vnd.dxf',
  dwg: 'application/vnd.dwg',

  // Database
  db: 'application/x-sqlite3',
  sqlite: 'application/x-sqlite3',
  mdb: 'application/x-access',
  accdb: 'application/x-access',

  // Fonts
  ttf: 'font/ttf',
  otf: 'font/otf',
  woff: 'font/woff',
  woff2: 'font/woff2',

  // JSON and XML
  json: 'application/json',
  xml: 'application/xml',

  // Other common formats
  rtf: 'application/rtf',
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  odp: 'application/vnd.oasis.opendocument.presentation',
}

/**
 * Get MIME type from file extension
 */
const getMimeTypeFromExtension = (filename: string): string => {
  const extension = filename.split('.').pop()?.toLowerCase()

  return extension ? EXTENSION_TO_MIME[extension] || 'text/plain' : 'text/plain'
}

/**
 * Get file extension from filename
 */
const getFileExtension = (filename: string): string => {
  const parts = filename.split('.')

  return parts.length > 1 ? parts.pop()!.toLowerCase() : ''
}

/**
 * Get icon path from MIME type
 */
const getIconFromMimeType = (mimeType: string): string => {
  const directMatch = MIME_TYPE_ICONS[mimeType]

  if (directMatch) return directMatch

  if (mimeType.startsWith('image/')) return '/assets/svg/file-types/picture.svg'
  if (mimeType.startsWith('video/')) return '/assets/svg/file-types/video.svg'
  if (mimeType.startsWith('audio/')) return '/assets/svg/file-types/audio.svg'
  if (mimeType.includes('pdf')) return '/assets/svg/file-types/pdf.svg'

  return '/assets/svg/file-text.svg'
}

/**
 * Get icon path from file extension
 */
const getIconFromExtension = (extension: string): string => {
  const mimeType = EXTENSION_TO_MIME[extension.toLowerCase()]

  return mimeType
    ? getIconFromMimeType(mimeType) || '/assets/svg/file-text.svg'
    : '/assets/svg/file-text.svg'
}

/**
 * Simple FileTypeIcon component
 */
export const FileTypeIcon: React.FC<FileTypeIconProps> = ({
  filename = '',
  mediaType,
  filetype,
  mimeType,
  size = 24,
}) => {
  // Handle folder case first
  if (mediaType === MediaType.FOLDER || filetype === MediaType.FOLDER) {
    return (
      <img
        alt="Folder"
        src="/assets/svg/folder.svg"
        style={{ width: `${size}px`, height: `${size}px` }}
      />
    )
  }

  // Try to get icon from MIME type first (highest priority)
  if (mimeType && mimeType !== 'folder') {
    const iconPath = getIconFromMimeType(mimeType)

    // if the mimeType has no icon, try to get it from the extension

    if (!iconPath) {
      const extension = getFileExtension(filename)
      const mimeType = getMimeTypeFromExtension(extension)

      const iconPath = getIconFromMimeType(mimeType)

      return <img alt="File" src={iconPath} style={{ width: `${size}px`, height: `${size}px` }} />
    }

    return <img alt="File" src={iconPath} style={{ width: `${size}px`, height: `${size}px` }} />
  }

  // Try to get icon from mediaType prop (second priority)
  if (mediaType && mediaType !== MediaType.FOLDER) {
    const MEDIA_TYPE_ICON: Record<MediaType, string> = {
      [MediaType.IMAGE]: '/assets/svg/file-types/picture.svg',
      [MediaType.VIDEO]: '/assets/svg/file-types/video.svg',
      [MediaType.AUDIO]: '/assets/svg/file-types/audio.svg',
      [MediaType.DOCUMENT]: '/assets/svg/file-types/docs.svg',
      [MediaType.SPREADSHEET]: '/assets/svg/file-types/excel.svg',
      [MediaType.PRESENTATION]: '/assets/svg/file-types/ppt.svg',
      [MediaType.PDF]: '/assets/svg/file-types/pdf.svg',
      [MediaType.ARCHIVE]: '/assets/svg/file-types/archives.svg',
      [MediaType.OTHER]: '/assets/svg/file-text.svg',
      [MediaType.FOLDER]: '/assets/svg/folder.svg',
    }

    const isKnownMediaType = (Object.values(MediaType) as string[]).includes(mediaType)
    const mType = isKnownMediaType ? (mediaType as MediaType) : undefined
    const iconPath = (mType ? MEDIA_TYPE_ICON[mType] : undefined) || '/assets/svg/file-text.svg'

    return <img alt="File" src={iconPath} style={{ width: `${size}px`, height: `${size}px` }} />
  }

  // Fallback to extension-based detection from filename
  const extension = getFileExtension(filename)

  if (extension) {
    const iconPath = getIconFromExtension(extension)

    return <img alt="File" src={iconPath} style={{ width: `${size}px`, height: `${size}px` }} />
  }

  // Final fallback to generic file icon
  return (
    <img
      alt="File"
      src="/assets/svg/file-text.svg"
      style={{ width: `${size}px`, height: `${size}px` }}
    />
  )
}

// Export utility functions
export {
  EXTENSION_TO_MIME,
  getFileExtension,
  getIconFromExtension,
  getIconFromMimeType,
  getMediaTypeByExtension,
  getMimeTypeFromExtension,
  MIME_TYPE_ICONS,
}
